import logoUrl from "@/assets/logo.png";
import { mediaUrl } from "@/lib/api";
import { useSiteConfig } from "@/hooks/useSiteConfig";

export const BRAND_LOGO_MIN = 24;
export const BRAND_LOGO_MAX = 96;
export const BRAND_LOGO_HEADER_DEFAULT = 45;
export const BRAND_LOGO_FOOTER_DEFAULT = 24;
export const BRAND_LOGO_MOBILE_DEFAULT = 40;

export function clampBrandLogoHeight(value, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(BRAND_LOGO_MIN, Math.min(BRAND_LOGO_MAX, Math.round(n)));
}

/**
 * MeraStartup / Grocode brand logo (image).
 * variant: "dark" (on dark surfaces -> wrapped in a light chip for contrast),
 *          "light" (on light surfaces -> image shown directly),
 *          "sidebar" (compact, dark surface)
 * placement: "header" uses mobile + desktop header heights from Super Admin;
 *            "footer" uses the footer height.
 *
 * Uses the Super Admin landing `brand.logo_url` unless `src` is passed.
 * Waits for site config so the bundled default is not flashed before the live logo.
 */
export default function Brand({ variant = "dark", size = "md", className = "", src, placement = "header" }) {
  const cfg = useSiteConfig();
  const onDark = variant === "dark" || variant === "sidebar";
  const sizeFallback = size === "sm" ? 24 : size === "lg" ? 44 : size === "xl" ? 45 : 32;
  const brand = cfg.brand || {};
  const headerH = clampBrandLogoHeight(brand.logo_height_header, size === "xl" ? 45 : sizeFallback);
  const footerH = clampBrandLogoHeight(brand.logo_height_footer, size === "sm" ? 24 : sizeFallback);
  const mobileH = clampBrandLogoHeight(brand.logo_height_mobile, Math.min(headerH, 40));

  const explicit = src !== undefined;
  const ready = explicit || cfg._loaded;
  const fromConfig = cfg.brand?.logo_url ? mediaUrl(cfg.brand.logo_url) : null;
  const imgSrc = explicit ? (src || logoUrl) : (fromConfig || logoUrl);

  const imgClass = placement === "footer"
    ? "brand-img-footer"
    : variant === "sidebar"
      ? "brand-img-sidebar"
      : "brand-img-header";
  const vars = {
    "--bh": `${headerH}px`,
    "--bf": `${footerH}px`,
    "--bm": `${mobileH}px`,
  };

  const img = ready ? (
    <img
      src={imgSrc}
      alt="MeraStartup — Powered by Grocode"
      className={`${imgClass} w-auto object-contain object-left ${variant === "sidebar" ? "max-w-full" : "max-w-[min(70vw,280px)]"}`}
      data-testid={placement === "footer" ? "brand-logo-footer" : "brand-logo-header"}
    />
  ) : (
    <span className="inline-block" style={{ height: variant === "sidebar" ? 28 : (placement === "footer" ? footerH : mobileH), width: variant === "sidebar" ? 120 : 96 }} aria-hidden />
  );

  if (variant === "sidebar") {
    return (
      <div className={`flex items-center bg-white rounded-lg px-2 py-1 shadow-sm w-full max-w-full min-w-0 overflow-hidden ${className}`} data-testid="brand-lockup">
        {img}
      </div>
    );
  }
  if (onDark) {
    return (
      <div className={`inline-flex items-center bg-white rounded-xl px-3 py-1.5 shadow-sm ${className}`} style={vars} data-testid="brand-lockup">
        {img}
      </div>
    );
  }
  return (
    <div className={`inline-flex items-center ${className}`} style={vars} data-testid="brand-lockup">
      {img}
    </div>
  );
}
